import {
  Backdrop,
  Button,
  CircularProgress,
  Fade,
  makeStyles,
  Modal,
  Typography,
} from "@material-ui/core";
import React, { useRef, useState } from "react";
import { uploadCSVFile } from "../../api/globalApi";

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:focus-visible": {
      outline: "none !important",
    },
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    borderRadius: theme.spacing(1),
    width: "600px",
    height: "300px",
    "&:focus-visible": {
      outline: "none !important",
    },
  },
  bodyWrapper: {
    padding: "20px",
    textAlign: "center",
    outline: "none !important",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    color: "var(--dark_blue)",
    "&: focus-visible": {
      outline: "none !important",
    },
  },
  submitButton: {
    border: "none",
    cursor: "pointer",
    padding: "4px 15px !important",
    minWidth: "75px !important",
    minHeight: "30px !important",
    borderRadius: "4px !important",
    textTransform: "capitalize !important",
    color: "#ffffff !important",
    fontWeight: "600 !important",
    backgroundColor: "var(--accent-color) !important",
    "&:focus, &:hover": {
      color: "#ffffff !important",
      backgroundColor: "var(--accent-color) !important",
    },
  },
  dropContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    width: "100%",
  },
}));

const BulkEmailUploadCSVModal = ({ open, onClose, onSuccessUpload }) => {
  const classes = useStyles();
  const [error, setError] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleFileUpload = (files) => {
    const selectedFile = files[0];
    if (selectedFile) {
      if (selectedFile.type === "text/csv") {
        setError("");
        setFile(selectedFile);
      } else {
        setError("Please upload a valid .csv file.");
      }
    }
  };

  const handleSubmit = async () => {
    if (!file) {
      setError("Please upload a .csv file before submitting.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    try {
      setUploading(true);
      const response = await uploadCSVFile(formData);

      if (response?.success) {
        window.showNotification(
          "success",
          response.message || "File uploaded successfully."
        );
        setFile(null);
        onSuccessUpload();
        onClose();
      } else {
        window.showNotification(
          "error",
          response?.message || "Upload failed. Please try again."
        );
      }
    } catch (err) {
      console.error("CSV upload failed:", err);
      window.showNotification(
        "error",
        "Something went wrong. Please try again."
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      className={classes.modal}
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Fade in={open}>
        <div
          style={{
            background: "white",
            borderRadius: "8px",
            width: "600px",
            height: "350px",
            margin: "100px auto",
            display: "flex",
            flexDirection: "column",
            position: "relative",
            padding: "20px",
          }}
        >
          {/* Header */}
          <div
            style={{
              borderBottom: "1px solid #ddd",
              paddingBottom: "10px",
              marginBottom: "15px",
              fontWeight: "bold",
              fontSize: "18px",
            }}
          >
            Upload CSV
          </div>

          {/* Body */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              marginBottom: "20px",
            }}
          >
            <div
              className={classes.dropContainer}
              style={{
                border: "2px dashed #aaa",
                padding: "20px",
                borderRadius: "8px",
                cursor: "pointer",
                width: "100%",
                textAlign: "center",
              }}
              onClick={() => inputRef.current.click()}
            >
              <input
                ref={inputRef}
                type="file"
                accept=".csv"
                style={{ display: "none" }}
                onChange={(event) => handleFileUpload(event.target.files)}
              />
              <Typography
                style={{ height: "unset" }}
                color="primary"
                variant="body2"
              >
                Click here to upload
              </Typography>
              <Typography style={{ height: "unset" }} variant="caption">
                Only .csv files are accepted
              </Typography>
            </div>

            {file && (
              <Typography
                variant="body2"
                style={{
                  marginTop: "10px",
                  color: "green",
                  fontWeight: 500,
                  height: "unset",
                }}
              >
                Uploaded file: {file.name}
              </Typography>
            )}
            {error && (
              <Typography
                variant="body2"
                style={{
                  marginTop: "10px",
                  color: "red",
                  fontWeight: 500,
                  height: "unset",
                }}
              >
                {error}
              </Typography>
            )}
          </div>

          {/* Footer */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
              borderTop: "1px solid #ddd",
              paddingTop: "10px",
            }}
          >
            <Button variant="outlined" onClick={onClose}>
              Close
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={uploading}
              className={classes.submitButton}
            >
              {uploading ? "Uploading..." : "Submit"}
            </Button>
          </div>

          {/* Uploading Overlay */}
          {uploading && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0, 0, 0, 0.7)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",

                alignItems: "center",
                borderRadius: "8px",
                zIndex: 10,
              }}
            >
              <CircularProgress style={{ color: "#fff" }} />
              <Typography
                variant="body2"
                style={{
                  marginTop: "70px",
                  fontSize: "20px",
                  color: "#fff",
                  fontWeight: 600,
                  height: "unset",
                }}
              >
                Uploading...
              </Typography>
            </div>
          )}
        </div>
      </Fade>
    </Modal>
  );
};

export default BulkEmailUploadCSVModal;
